// Content Generation API Service
// Handles AI content creation, outline generation, and image generation

import { API_BASE_URL } from '../config/api';

/**
 * Generate content outline from topic
 * @param {string} topic - Content topic/theme
 * @param {string} skillId - Selected skill template ID
 * @param {string} token - Auth token
 * @param {number} pageCount - Number of pages to generate
 * @returns {Promise<Object>} - Generated outline
 */
export const generateOutline = async (topic, skillId, token, pageCount = 7) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/outline/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ topic, skill_name: skillId, page_count: pageCount })
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || data.error || '大纲生成失败');
    }
    return data;
};

/**
 * Generate copywriting/titles/tags for an existing outline task
 * @param {string} taskId - Content task ID
 * @param {string} topic - Topic
 * @param {string} outline - Outline text
 * @param {string} token - Auth token
 * @returns {Promise<Object>}
 */
export const generateContent = async (taskId, topic, outline, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/generate/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            task_id: taskId,
            topic,
            outline,
        })
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || data.error || '文案生成失败');
    }
    return data;
};

/**
 * Generate images from outline pages
 * @param {string} taskId - Content task ID
 * @param {Array} pages - Outline pages to generate images for
 * @param {number} imageCount - Number of images to generate (1-9)
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Generated images info
 */
export const generateImages = async (taskId, pages, imageCount, token, referenceImageUrl = '') => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/image/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            task_id: taskId,
            pages,
            image_count: imageCount,
            reference_image_url: referenceImageUrl
        })
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || data.error || '图片生成失败');
    }
    return data;
};

/**
 * Create async content task (outline + copywriting by worker)
 * @param {string} topic
 * @param {string} skillName
 * @param {string} token
 * @returns {Promise<{task_id: string, status: string, estimated_cost: number}>}
 */
export const createContentTask = async (topic, skillName, token) => {
    const body = { topic, skill_name: skillName };
    console.log('[contentService] createContentTask 请求:', body);
    const response = await fetch(`${API_BASE_URL}/api/v1/content/tasks/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    });

    const data = await response.json();
    console.log('[contentService] createContentTask 响应:', response.status, data);
    if (!response.ok) {
        throw new Error(data.message || data.error || '创建内容任务失败');
    }
    return data;
};

/**
 * Query async content task result endpoint
 * @param {string} taskId
 * @param {string} token
 * @returns {Promise<Object>}
 */
export const getContentTaskResult = async (taskId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/tasks/${taskId}/result/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    const data = await response.json();
    if (response.status === 202) {
        return data;
    }
    if (!response.ok) {
        throw new Error(data.message || data.error || '获取任务结果失败');
    }
    return data;
};

/**
 * Update task outline (PATCH draft)
 * @param {string} taskId
 * @param {string} outline
 * @param {string} token
 * @returns {Promise<Object>}
 */
export const updateTaskOutline = async (taskId, outline, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/tasks/${taskId}/`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ outline }),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || data.error || '更新大纲失败');
    }
    return data;
};

/**
 * Confirm and generate images for a task via grsai
 * @param {string} taskId
 * @param {string} outline - Latest outline text
 * @param {string} token
 * @param {string} aspectRatio
 * @returns {Promise<Object>}
 */
export const confirmGenerateImages = async (taskId, outline, token, aspectRatio = '3:4') => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/tasks/${taskId}/generate-images/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ outline, aspect_ratio: aspectRatio }),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || data.error || '图片生成失败');
    }
    return data;
};

/**
 * Regenerate a specific image
 * @param {string} taskId - Content task ID
 * @param {number} imageIndexOrPageNumber - 0-based image index or 1-based page number
 * @param {string} token - Auth token
 * @param {{usePageNumber?: boolean}} options
 * @returns {Promise<Object>} - New image info
 */
export const regenerateImage = async (taskId, imageIndexOrPageNumber, token, options = {}) => {
    const numericValue = Number(imageIndexOrPageNumber);
    const pageNumber = options.usePageNumber ? numericValue : (numericValue + 1);
    if (!Number.isFinite(pageNumber) || pageNumber < 1) {
        throw new Error('无效页码');
    }

    const payload = { task_id: taskId, page_number: pageNumber };
    const preferred = `${API_BASE_URL}/api/v1/content/tasks/${taskId}/regenerate-image/`;
    const fallback = `${API_BASE_URL}/api/v1/content/image/regenerate/`;

    const preferredRes = await fetch(preferred, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
    });

    if (preferredRes.status !== 404) {
        const preferredData = await preferredRes.json();
        if (!preferredRes.ok) {
            throw new Error(preferredData.message || preferredData.error || '图片重新生成失败');
        }
        return preferredData;
    }

    const fallbackRes = await fetch(fallback, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
    });
    const fallbackData = await fallbackRes.json();
    if (!fallbackRes.ok) {
        throw new Error(fallbackData.message || fallbackData.error || '图片重新生成失败');
    }
    return fallbackData;
};

/**
 * Regenerate all or selected images in a task
 * @param {string} taskId
 * @param {number[]} imageIndexes - 0-based indexes
 * @param {string} token
 * @returns {Promise<Object>}
 */
export const regenerateImageBatch = async (taskId, imageIndexes = [], token) => {
    const pageNumbers = imageIndexes.map(i => i + 1);
    const payload = {
        task_id: taskId,
        all: pageNumbers.length === 0,
        page_numbers: pageNumbers.length ? pageNumbers : undefined,
    };
    const preferred = `${API_BASE_URL}/api/v1/content/tasks/${taskId}/regenerate-images/`;
    const fallback = `${API_BASE_URL}/api/v1/content/image/regenerate-batch/`;

    const preferredRes = await fetch(preferred, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
    });

    if (preferredRes.status !== 404) {
        const preferredData = await preferredRes.json();
        if (!preferredRes.ok) {
            throw new Error(preferredData.message || preferredData.error || '整组重生图失败');
        }
        return preferredData;
    }

    const fallbackRes = await fetch(fallback, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
    });
    const fallbackData = await fallbackRes.json();
    if (!fallbackRes.ok) {
        throw new Error(fallbackData.message || fallbackData.error || '整组重生图失败');
    }
    return fallbackData;
};

/**
 * Get content task status
 * @param {string} taskId - Content task ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Task status and progress
 */
export const getContentTaskStatus = async (taskId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/tasks/${taskId}/`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '获取任务状态失败');
    }

    return response.json();
};

/**
 * Get user's content history
 * @param {string} token - Auth token
 * @param {Object} options - Query options (page, status filter)
 * @returns {Promise<Object>} - List of content tasks
 */
export const getContentHistory = async (token, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.status) params.append('status', options.status);

    const response = await fetch(
        `${API_BASE_URL}/api/v1/content/tasks/?${params.toString()}`,
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || data.error || '获取历史记录失败');
    }
    return data;
};

/**
 * Delete a content task
 * @param {string} taskId - Content task ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteContentTask = async (taskId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/tasks/${taskId}/`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || data.error || '删除失败');
    }
    return { success: true };
};

/**
 * Generate image via grsai nano-banana API (supports reference image)
 * @param {string} prompt - Image generation prompt
 * @param {string} token - Auth token
 * @param {string} referenceImageUrl - Optional reference image URL
 * @returns {Promise<Object>} - { image_url, model }
 */
export const generateImageGrsai = async (prompt, token, referenceImageUrl = '') => {
    const response = await fetch(`${API_BASE_URL}/api/v1/content/image/grsai/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            prompt,
            reference_image_url: referenceImageUrl,
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '图片生成失败');
    }

    return response.json();
};

/**
 * Download task images via backend (ZIP or single image).
 * @param {string} taskId - Content task ID
 * @param {string} token - Auth token
 * @param {number|null} pageNumber - page_number for single download, null for ZIP all
 */
export const downloadTaskImages = async (taskId, token, pageNumber = null) => {
    const params = pageNumber !== null ? `?page=${pageNumber}` : '';
    const response = await fetch(
        `${API_BASE_URL}/api/v1/content/tasks/${taskId}/download-images/${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
    );
    if (!response.ok) {
        let msg = '下载失败';
        try { const err = await response.json(); msg = err.message || msg; } catch { }
        throw new Error(msg);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : (pageNumber !== null ? `image_${pageNumber}.png` : `${taskId}_images.zip`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

/**
 * Calculate estimated credits for content generation
 * @param {number} imageCount - Number of images
 * @returns {number} - Total credit cost
 */
export const calculateCreditCost = (imageCount = 1) => {
    const OUTLINE_COST = 1;
    const CONTENT_COST = 1;
    const IMAGE_COST = 6;
    return OUTLINE_COST + CONTENT_COST + (IMAGE_COST * imageCount);
};

const contentService = {
    createContentTask,
    getContentTaskResult,
    generateOutline,
    generateContent,
    generateImages,
    regenerateImage,
    regenerateImageBatch,
    generateImageGrsai,
    getContentTaskStatus,
    getContentHistory,
    deleteContentTask,
    calculateCreditCost
};

export default contentService;
